import { ChartCard, ChartCardContent, ChartCardHeader } from '@pique/component';
import dayjs from 'dayjs';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as styles from './ActivityGraph.css';
import {
  type ActivityGraphMode,
  useActivityGraphData,
} from './useActivityGraphData';

const GREEN_SCALE = [
  '#ebedf0',
  '#b6e8c8',
  '#6dd698',
  '#2ecc71',
  '#1fa855',
];

const DAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
const CELL_SIZE = 12;
const CELL_GAP = 3;
const WEEK_COUNT = 53;
const MIN_MAX_FOR_SCALE = 30;

function getColor(minutes: number, max: number): string {
  if (minutes === 0) return GREEN_SCALE[0]!;
  const effectiveMax = Math.max(max, MIN_MAX_FOR_SCALE);
  const ratio = minutes / effectiveMax;
  if (ratio <= 0.2) return GREEN_SCALE[1]!;
  if (ratio <= 0.45) return GREEN_SCALE[2]!;
  if (ratio <= 0.75) return GREEN_SCALE[3]!;
  return GREEN_SCALE[4]!;
}

const formatTime = (minutes: number) => {
  if (minutes === 0) return '0m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

interface HoveredCell {
  date: string;
  minutes: number;
  color: string;
}

const HoverTooltip: React.FC<{ cell: HoveredCell }> = ({ cell }) => (
  <div className={styles.tooltipFloating}>
    <div className={styles.tooltipCard}>
      <div className={styles.tooltipDot} style={{ background: cell.color }} />
      <span className={styles.tooltipValue}>
        {formatTime(cell.minutes)}
      </span>
      <span className={styles.tooltipDate}>
        {dayjs(cell.date).format('MMM D, YYYY')}
      </span>
    </div>
  </div>
);

// ── Week view (7 large cells) ──

const WeekView: React.FC<{
  days: { date: string; minutes: number }[];
  maxMinutes: number;
  hovered: HoveredCell | null;
  onMouseEnter: (date: string, minutes: number, color: string) => void;
  onMouseLeave: () => void;
}> = ({ days, maxMinutes, hovered, onMouseEnter, onMouseLeave }) => {
  const WEEK_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const lastSeven = days.slice(-7);

  return (
    <div className={styles.weekGrid}>
      {lastSeven.map((day, i) => {
        const d = dayjs(day.date);
        const isToday = d.isSame(dayjs(), 'day');
        const label = isToday
          ? 'Today'
          : WEEK_LABELS[d.day() === 0 ? 6 : d.day() - 1];
        const color = getColor(day.minutes, maxMinutes);
        const isHovered = hovered?.date === day.date;
        return (
          <div
            key={day.date}
            className={styles.weekDayColumn}
            style={{ animationDelay: `${i * 50}ms` }}
          >
            <span className={styles.weekDayLabel}>{label}</span>
            <div className={styles.cellAnchor}>
              <div
                className={styles.weekCell}
                style={{ backgroundColor: color }}
                data-selected={isHovered}
                onMouseEnter={() => onMouseEnter(day.date, day.minutes, color)}
                onMouseLeave={onMouseLeave}
              />
              {isHovered && day.minutes > 0 && (
                <HoverTooltip cell={hovered} />
              )}
            </div>
            <span className={styles.weekMinutesLabel}>
              {day.minutes > 0 ? formatTime(day.minutes) : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ── Year view (GitHub-style grid) ──

const YearView: React.FC<{
  days: { date: string; minutes: number }[];
  maxMinutes: number;
  hovered: HoveredCell | null;
  onMouseEnter: (date: string, minutes: number, color: string) => void;
  onMouseLeave: () => void;
}> = ({ days, maxMinutes, hovered, onMouseEnter, onMouseLeave }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ left: scrollRef.current.scrollWidth });
  }, []);

  const { weeks, months } = useMemo(() => {
    const dayMap = new Map(days.map(d => [d.date, d.minutes]));
    const today = dayjs();
    const start = today.subtract(364, 'day');
    let cursor = start.startOf('week');

    const weeksArr: { date: string; color: string; minutes: number }[][] = [];
    for (let w = 0; w < WEEK_COUNT; w++) {
      const week: { date: string; color: string; minutes: number }[] = [];
      for (let d = 0; d < 7; d++) {
        const dateKey = cursor.format('YYYY-MM-DD');
        const mins = dayMap.get(dateKey) ?? 0;
        week.push({
          date: dateKey,
          color: getColor(mins, maxMinutes),
          minutes: mins,
        });
        cursor = cursor.add(1, 'day');
      }
      weeksArr.push(week);
    }

    const monthLabels: { label: string; index: number }[] = [];
    let prevMonth = -1;
    for (let i = 0; i < WEEK_COUNT; i++) {
      const firstDay = dayjs(weeksArr[i]![0]!.date);
      const month = firstDay.month();
      if (month !== prevMonth) {
        monthLabels.push({ label: firstDay.format('MMM'), index: i });
        prevMonth = month;
      }
    }

    return { weeks: weeksArr, months: monthLabels };
  }, [days, maxMinutes]);

  const gridWidth = WEEK_COUNT * (CELL_SIZE + CELL_GAP);

  return (
    <div className={styles.graphArea}>
      <div className={styles.dayLabelsColumn}>
        {DAY_LABELS.map((label, i) => (
          <div
            key={i}
            className={styles.dayLabel}
            style={{ height: CELL_SIZE + CELL_GAP }}
          >
            {label}
          </div>
        ))}
      </div>
      <div ref={scrollRef} className={styles.scrollContainer}>
        <div>
          <div className={styles.monthRow} style={{ width: gridWidth }}>
            {months.map((m, i) => (
              <span
                key={i}
                className={styles.monthLabel}
                style={{ left: m.index * (CELL_SIZE + CELL_GAP) }}
              >
                {m.label}
              </span>
            ))}
          </div>
          <div className={styles.grid}>
            {weeks.map((week, wi) => (
              <div key={wi} className={styles.weekColumn}>
                {week.map((c, di) => {
                  const isHovered = hovered?.date === c.date;
                  return (
                    <div key={di} className={styles.cellAnchor}>
                      <div
                        className={styles.cell}
                        style={{ backgroundColor: c.color }}
                        data-selected={isHovered}
                        onMouseEnter={() =>
                          onMouseEnter(c.date, c.minutes, c.color)
                        }
                        onMouseLeave={onMouseLeave}
                      />
                      {isHovered && c.minutes > 0 && (
                        <HoverTooltip cell={hovered} />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ActivityGraph: React.FC = () => {
  const [mode, setMode] = useState<ActivityGraphMode>('week');
  const { days, totalMinutes, maxMinutes } = useActivityGraphData(mode);
  const [hovered, setHovered] = useState<HoveredCell | null>(null);

  const handleMouseEnter = useCallback(
    (date: string, minutes: number, color: string) => {
      setHovered({ date, minutes, color });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setHovered(null);
  }, []);

  const handleToggle = useCallback(() => {
    setMode(prev => (prev === 'week' ? 'year' : 'week'));
    setHovered(null);
  }, []);

  const displayTotal =
    mode === 'week'
      ? days.slice(-7).reduce((s, d) => s + d.minutes, 0)
      : totalMinutes;

  const periodLabel = mode === 'week' ? 'this week' : 'this year';

  return (
    <ChartCard style={{ border: 'none', boxShadow: 'none', padding: '4px 6px' }}>
      <ChartCardHeader
        style={{ padding: '12px 16px 8px 16px', borderBottom: 'none' }}
      >
        <div className={styles.headerRow}>
          <div className={styles.statsRow}>
            <span className={styles.statValue}>
              {formatTime(displayTotal)}
            </span>
            <span
              className={styles.periodToggle}
              onClick={handleToggle}
              role="button"
              tabIndex={0}
            >
              {periodLabel}
            </span>
          </div>
        </div>
      </ChartCardHeader>
      <ChartCardContent style={{ padding: '0 16px 12px 16px' }}>
        {mode === 'year' ? (
          <YearView
            days={days}
            maxMinutes={maxMinutes}
            hovered={hovered}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        ) : (
          <WeekView
            days={days}
            maxMinutes={maxMinutes}
            hovered={hovered}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
        <div className={styles.legend}>
          <span className={styles.legendText}>Less</span>
          {GREEN_SCALE.map((color, i) => (
            <div
              key={i}
              className={styles.legendCell}
              style={{ backgroundColor: color }}
            />
          ))}
          <span className={styles.legendText}>More</span>
        </div>
      </ChartCardContent>
    </ChartCard>
  );
};
